import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslate } from '../TranslationText';
import TranslationsKeys from '../../TranslationsKeys';

/**
 * Clean text input with React Hook Form integration.
 * Supports: text, email, number, textarea
 *
 * @param {string} name - Form field name (used for register)
 * @param {string} label - Label text
 * @param {string} type - HTML input type: 'text' | 'email' | 'number' | 'textarea'
 * @param {string} placeholder - Input placeholder
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable input
 * @param {object} rules - React Hook Form validation rules
 * @param {number} maxLength - Max character length
 * @param {number} rows - Textarea rows (default: 4)
 * @param {boolean} hideLabel - Hide the label
 * @param {object} style - Additional inline styles
 */
export default function FormTextInput({
    name,
    label,
    type = 'text',
    placeholder = '',
    required = false,
    disabled = false,
    rules,
    maxLength,
    rows = 4,
    hideLabel = false,
    noMargin = false,
    hideError = false,
    noValidate = false,
    inputMode,
    style = {},
    ...props
}) {
    const {
        register,
        control,
        formState: { errors },
    } = useFormContext();
    const { translate } = useTranslate();
    const fieldValue = useWatch({ control, name });

    const validationRules = {
        ...(required && !noValidate && {
            required: translate(TranslationsKeys.REQUIRED_FIELD, 'Required Field'),
        }),
        ...(maxLength && {
            maxLength: {
                value: maxLength,
                message: translate(TranslationsKeys.MAX_CHARACTERS, `Max ${maxLength} characters`),
            },
        }),
        ...rules,
    };

    const { ref, onChange, onBlur, ...registerRest } = register(name, validationRules);

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);
    const isRtl = (localStorage.getItem('eahsLang') ?? 'en') === 'ar';

    const isTextarea = type === 'textarea';

    const inputProps = {
        ...props,
        ref,
        id: name,
        name,
        className: `form-control rounded${fieldError ? ' is-invalid' : ''}`,
        placeholder,
        disabled,
        value: fieldValue ?? '',
        onChange,
        onBlur,
        dir: isRtl ? 'rtl' : 'ltr',
        style: {
            ...(isTextarea ? {
                height: style?.height || (rows ? `${Math.max(rows * 26, 120)}px` : '130px'),
            } : {}),
            ...style,
            textAlign: isRtl ? 'right' : 'left',
        },
        ...(inputMode && { inputMode }),
        ...registerRest,
        ...(maxLength && { maxLength }),
    };

    return (
        <div
            id={`field-container-${name}`}
            data-field={name}
            className={noMargin ? '' : 'mb-2'}
        >
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 2px' }}>*</i>}
                </label>
            )}

            {type === 'textarea' ? (
                <textarea {...inputProps} rows={rows} />
            ) : (
                <input {...inputProps} type={type} />
            )}

            {!hideError && fieldError && (
                <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {fieldError.message || translate(TranslationsKeys.REQUIRED_FIELD, 'Required Field')}
                </div>
            )}
        </div>
    );
}
